<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { useGetAllLegends, useGetUsers } from "@/stores/queries";
import SkeletonList from "../UI/skeleton/SkeletonList.vue";
import { useRouter } from "vue-router";
import { computed, ref } from "vue";
import LegendActions from "./LegendActions.vue";
import LegendUsersSelector from "./LegendUsersSelector.vue";
import DRBtnIcon from "../UI/DRBtnIcon.vue";
import DRBtn from "../UI/DRBtn.vue";
import { useScreen } from "@/composable/useResponsive";
import OrganizationPageLayout from "@/components/organization/OrganizationPageLayout.vue";
import UserState from "@/stores/UserState";

const { t } = useTranslation();
const { screen } = useScreen();
const router = useRouter();

const canManageLegends = computed(() =>
  ["MANAGER", "ADMIN", "SUPERADMIN"].includes(UserState.data?.role ?? "")
);

const { data: legends, isLoading } = useGetAllLegends();
const { data: users } = useGetUsers();

const handleCreate = () => {
  router.push({ name: "legend-create" });
};

const query = ref<string>("");
const selectedUserId = ref<number | undefined>(undefined);
const isFilters = computed(() => {
  return !!query.value?.length || !!selectedUserId.value;
});

const removeFilters = () => {
  query.value = "";
  selectedUserId.value = undefined;
};

const filteredLegends = computed(() => {
  if (!legends.value) return [];
  let res = [...legends.value];

  if (query.value.length) {
    res = res.filter(
      (legend) =>
        !query.value?.length ||
        legend.name.toLowerCase().includes(query.value.toLowerCase())
    );
  }
  if (selectedUserId.value) {
    res = res.filter((legend) =>
      legend.users?.some((user) => user.id === selectedUserId.value)
    );
  }

  return res;
});
</script>

<template>
  <OrganizationPageLayout
    :title="t('dashboard:navigation.legends')"
    :description="t('dashboard:navigation.legends-description')"
  >
    <template v-if="canManageLegends" #actions>
      <DRBtn
        :label="t('dashboard:create.legend')"
        icon="bi-plus-lg"
        @click="handleCreate"
      />
    </template>

    <template v-if="canManageLegends" #toolbar>
      <el-input
        v-model="query"
        :placeholder="t('search')"
        :style="{ width: screen.gt.md ? '200px' : '150px' }"
        clearable
      >
        <template #prefix>
          <i class="bi-search"></i>
        </template>
      </el-input>
      <el-select
        v-model="selectedUserId"
        :placeholder="t('filter')"
        :style="{ width: screen.gt.md ? '200px' : '150px' }"
        clearable
      >
        <el-option
          v-for="user in users"
          :key="user.id"
          :label="user.full_name"
          :value="user.id"
        />
      </el-select>
      <DRBtnIcon
        v-if="isFilters"
        variant="outline-danger"
        icon="bi-x-lg"
        :size="30"
        @click="removeFilters"
      />
    </template>

    <template v-if="!canManageLegends">
      <div class="py-5">
        <p class="fs-4 text-center text-grey">
          {{ t("dashboard:legends.no-access") }}
        </p>
      </div>
    </template>
    <template v-else>
      <SkeletonList v-if="isLoading" />
      <div v-else-if="legends?.length" class="d-flex flex-column gap-2">
        <div class="rounded overflow-hidden bordered">
          <el-table
            :data="filteredLegends"
            style="width: 100%"
            :row-style="{ height: '65px' }"
            header-cell-class-name="fw-normal"
          >
            <el-table-column
              prop="name"
              :label="t('dashboard:name.of.legend')"
              min-width="80"
              show-overflow-tooltip
            />

            <el-table-column
              prop="id"
              :label="t('dashboard:legends.table.column.accessRights')"
            >
              <template #default="scope">
                <LegendUsersSelector :key="scope.row.id" :legend="scope.row" />
              </template>
            </el-table-column>

            <el-table-column>
              <template #default="scope">
                <div class="d-flex justify-content-end align-items-center">
                  <LegendActions :key="scope.row.id" :legend="scope.row" />
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div v-else class="py-5">
        <p class="fs-4 text-center text-lg-center text-grey">
          {{ t("dashboard:no.legend") }}
        </p>
      </div>
    </template>
  </OrganizationPageLayout>
</template>
